"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RichTextEditor } from "@/components/RichTextEditor";
import type { SectionsOutput } from "@/interfaces/sections";
import { useState, useEffect } from "react";

interface SectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (section: SectionsOutput) => void;
  section: SectionsOutput | null;
}

export function SectionModal({
  isOpen,
  onClose,
  onSave,
  section,
}: SectionModalProps) {
  const [currentSection, setCurrentSection] = useState<SectionsOutput | null>(
    section
  );

  useEffect(() => {
    setCurrentSection(section);
  }, [section]);

  const handleSave = () => {
    if (!currentSection || !currentSection.name.trim()) return;
    onSave(currentSection);
  };

  if (!currentSection) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="bg-gradient-to-r from-[#EE7B11] to-[#f59642] -mx-6 -mt-6 px-6 py-4 rounded-t-lg">
          <DialogTitle className="text-white text-xl">
            {section?.id ? "Editar Seção" : "Nova Seção"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="section-name" className="text-sm font-medium">
              Nome da Seção *
            </Label>
            <Input
              id="section-name"
              value={currentSection.name}
              required
              className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
              onChange={(e) =>
                setCurrentSection({ ...currentSection, name: e.target.value })
              }
            />
          </div>
          <div className="grid gap-2">
            <Label
              htmlFor="section-description"
              className="text-sm font-medium"
            >
              Descrição
            </Label>
            <RichTextEditor
              value={currentSection.description}
              onChange={(value) =>
                setCurrentSection({
                  ...currentSection,
                  description: value,
                })
              }
              placeholder="Descreva a seção..."
            />
          </div>
          <div className="flex items-center gap-2 pt-2">
            <Switch
              id="section-visible"
              checked={currentSection.isVisible}
              onCheckedChange={(checked) =>
                setCurrentSection({ ...currentSection, isVisible: checked })
              }
              className="data-[state=checked]:bg-[#EE7B11]"
            />
            <Label htmlFor="section-visible" className="text-sm font-medium">
              Visível para clientes
            </Label>
          </div>
        </div>
        <DialogFooter className="border-t pt-4 mt-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-gray-300"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={!currentSection.name.trim()}
            className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
          >
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
